import { Injectable } from '@angular/core';
import { omitBy, isUndefined } from 'lodash';
import { of, map, tap, Observable } from 'rxjs';
import { QualificationOutcome } from 'src/app/core/enums/lead/qualification-outcome.enum';
import { Contact, ContactType } from 'src/app/core/models/common/contact.model';
import { FiscalId, FiscalIdKind } from 'src/app/core/models/common/fiscal-id.model';
import { Municipality } from 'src/app/core/models/common/municipality.model';
import { Province } from 'src/app/core/models/common/province.model';
import { LeadRawValue } from 'src/app/core/models/lead/lead-raw-value.model';
import { Lead } from 'src/app/core/models/lead/lead.model';
import { LeadChangeRequestDto } from 'src/app/infrastructure/lobby-proxy/DTOs/lead-change.dto';
import { BusinessSectorProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-business-sector/business-sector-proxy.service';
import { ConfigurationProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-configuration/configuration-proxy.service';
import { LeadProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-lead/lead-proxy.service';
import { ItalianRegistryProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-registry/italian-registry-proxy.service';
import { businessSectorDtoToBusinessSector } from 'src/app/shared/mappings/common-model-dto/business-sector-dto.mapping';
import { contactsToDtoChangesContainerContacts } from 'src/app/shared/mappings/common-model-dto/contact-dto.mapping';
import { comuneDtoToMunicipality } from 'src/app/shared/mappings/common-model-dto/municipality-dto.mapping';
import { provinceDtoToProvince } from 'src/app/shared/mappings/common-model-dto/province-dto.mapping';
import { leadDtoToLead } from 'src/app/shared/mappings/lead-model-dto/lead-dto.mapping';
import { fiscalIdsToDtoChangesContainerCodiceFiscale, fiscalIdsToDtoChangesContainerPartitaIva } from 'src/app/shared/mappings/lead-model-dto/lead-fiscal-id-dto.mapping';
import { leadRawValuesToDtoChangesContainerRawValues } from 'src/app/shared/mappings/lead-model-dto/lead-raw-value-dto.mapping';
import { dryrunErrors, LeadDryrunError } from 'src/app/shared/utils/pipes/dryrun-lead-error.pipe';
import { throwIfLobbyErrorCode, throwIfLobbyStatusErrorCode } from 'src/app/shared/utils/pipes/throwIfLobbyErrorCode.pipe';

@Injectable({
  providedIn: 'root'
})
export class LeadInfoFormService {

  private municipalityCache = new Map<number, Municipality>()
  private provinceCache = new Map<number, Province>()

  constructor(private readonly leadProxyService: LeadProxyService,
    private readonly italianRegistryProxyService: ItalianRegistryProxyService,
    private readonly businessSectorProxyService: BusinessSectorProxyService) { }

  getMunicipality$(municipalityId: number) {

    const cachedValue = this.municipalityCache.get(municipalityId)
    if (cachedValue) return of(cachedValue)

    return this.italianRegistryProxyService.getMunicipalityById$(municipalityId).pipe(
      throwIfLobbyErrorCode,
      map(dto => comuneDtoToMunicipality(dto.data)!),
      tap(municipality => this.municipalityCache.set(municipality.id, municipality))
    )
  }

  getProvinces$(provinceId: number) {

    const cachedValue = this.provinceCache.get(provinceId)
    if (cachedValue) return of(cachedValue)

    return this.italianRegistryProxyService.getProvinciaById$(provinceId).pipe(
      throwIfLobbyErrorCode,
      map(dto => provinceDtoToProvince(dto.data)),
      tap(province => this.provinceCache.set(province!.id, province!))
    )
  }

  getBusinessSector$(businessSectorId: number) {
    return this.businessSectorProxyService.getBusinessSectorById$(businessSectorId).pipe(
      throwIfLobbyErrorCode,
      map(dto => businessSectorDtoToBusinessSector(dto.data)!)
    )
  }

  rejectLead$(leadId: number, outcome: QualificationOutcome.Spam | QualificationOutcome.AlreadyCustomer = QualificationOutcome.Spam): Observable<void> {
    return this.leadProxyService.rejectLead$(leadId, outcome).pipe(throwIfLobbyStatusErrorCode, map(() => void 0))
  }

  requestLeadAutoOffer$(leadId: number): Observable<void> {
    return this.leadProxyService.requestLeadAutoOffer$(leadId).pipe(throwIfLobbyStatusErrorCode, map(() => void 0))
  }

  updateLeadInfo$(newLeadInfo: Lead, oldLeadInfo: Lead): Observable<void> {

    const rawvaluesChanges = this.getRawvaluesChanges(newLeadInfo, oldLeadInfo)
    const emailContactsChanges = this.getContactsChanges(newLeadInfo, oldLeadInfo, ContactType.Email)
    const phoneContactsChanges = this.getContactsChanges(newLeadInfo, oldLeadInfo, ContactType.Phone)
    const pivaChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.PartitaIva)
    const italianFiscalCodePhysicsChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.CfPersonaFisica)
    const italianFiscalCodeLegalChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.CfPersonaGiuridica)

    const patchDto: LeadChangeRequestDto = {
      provinciaId: newLeadInfo.province?.id != oldLeadInfo.province?.id ? newLeadInfo.province?.id ?? null : undefined,
      comuneId: newLeadInfo.municipality?.id != oldLeadInfo.municipality?.id ? newLeadInfo.municipality?.id ?? null : undefined,
      description: newLeadInfo.description != oldLeadInfo.description ? newLeadInfo.description : undefined,
      comment: newLeadInfo.comment != oldLeadInfo.comment ? newLeadInfo.comment : undefined,
      businessName: newLeadInfo.businessName != oldLeadInfo.businessName ? newLeadInfo.businessName : undefined,
      businessSectorId: newLeadInfo.businessSector?.id != oldLeadInfo.businessSector?.id ? newLeadInfo.businessSector?.id ?? null : undefined,
      cfPersoneFisiche: fiscalIdsToDtoChangesContainerCodiceFiscale(italianFiscalCodePhysicsChanges[0], italianFiscalCodePhysicsChanges[1], italianFiscalCodePhysicsChanges[2]),
      cfPersoneGiuridiche: fiscalIdsToDtoChangesContainerCodiceFiscale(italianFiscalCodeLegalChanges[0], italianFiscalCodeLegalChanges[1], italianFiscalCodeLegalChanges[2]),
      partiteIva: fiscalIdsToDtoChangesContainerPartitaIva(pivaChanges[0], pivaChanges[1], pivaChanges[2]),
      emails: contactsToDtoChangesContainerContacts(emailContactsChanges[0], emailContactsChanges[1], emailContactsChanges[2]),
      phoneNumbers: contactsToDtoChangesContainerContacts(phoneContactsChanges[0], phoneContactsChanges[1], phoneContactsChanges[2]),
      rawValues: leadRawValuesToDtoChangesContainerRawValues(rawvaluesChanges[0], rawvaluesChanges[1], rawvaluesChanges[2]),
    }

    return this.leadProxyService.patchLead$(newLeadInfo.id, newLeadInfo.version, omitBy(patchDto, isUndefined)).pipe(
      throwIfLobbyErrorCode,
      map(() => void 0)
    )
  }

  dryrunLeadInfo$(newLeadInfo: Lead, oldLeadInfo: Lead): Observable<LeadDryrunError[] | Lead> {

    const rawvaluesChanges = this.getRawvaluesChanges(newLeadInfo, oldLeadInfo)
    const emailContactsChanges = this.getContactsChanges(newLeadInfo, oldLeadInfo, ContactType.Email)
    const phoneContactsChanges = this.getContactsChanges(newLeadInfo, oldLeadInfo, ContactType.Phone)
    const pivaChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.PartitaIva)
    const italianFiscalCodePhysicsChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.CfPersonaFisica)
    const italianFiscalCodeLegalChanges = this.getFiscalIdsChanges(newLeadInfo, oldLeadInfo, FiscalIdKind.CfPersonaGiuridica)

    const patchDto: Partial<LeadChangeRequestDto> = {
      provinciaId: newLeadInfo.province?.id != oldLeadInfo.province?.id ? newLeadInfo.province?.id ?? null : undefined,
      comuneId: newLeadInfo.municipality?.id != oldLeadInfo.municipality?.id ? newLeadInfo.municipality?.id ?? null : undefined,
      description: newLeadInfo.description != oldLeadInfo.description ? newLeadInfo.description : undefined,
      comment: newLeadInfo.comment != oldLeadInfo.comment ? newLeadInfo.comment : undefined,
      businessSectorId: newLeadInfo.businessSector?.id != oldLeadInfo.businessSector?.id ? newLeadInfo.businessSector?.id ?? null : undefined,
      cfPersoneFisiche: fiscalIdsToDtoChangesContainerCodiceFiscale(italianFiscalCodePhysicsChanges[0], italianFiscalCodePhysicsChanges[1], italianFiscalCodePhysicsChanges[2]),
      cfPersoneGiuridiche: fiscalIdsToDtoChangesContainerCodiceFiscale(italianFiscalCodeLegalChanges[0], italianFiscalCodeLegalChanges[1], italianFiscalCodeLegalChanges[2]),
      partiteIva: fiscalIdsToDtoChangesContainerPartitaIva(pivaChanges[0], pivaChanges[1], pivaChanges[2]),
      emails: contactsToDtoChangesContainerContacts(emailContactsChanges[0], emailContactsChanges[1], emailContactsChanges[2]),
      phoneNumbers: contactsToDtoChangesContainerContacts(phoneContactsChanges[0], phoneContactsChanges[1], phoneContactsChanges[2]),
      rawValues: leadRawValuesToDtoChangesContainerRawValues(rawvaluesChanges[0], rawvaluesChanges[1], rawvaluesChanges[2]),
    }


    return this.leadProxyService.patchLead$(newLeadInfo.id, newLeadInfo.version, omitBy(patchDto, isUndefined), true).pipe(
      map(dto => leadDtoToLead(dto.data.lead)),
      dryrunErrors
    )
  }

  private getRawvaluesChanges(newLeadInfo: Lead, oldLeadInfo: Lead): [created: LeadRawValue[], changed: LeadRawValue[], removed: LeadRawValue[]] {
    let created: LeadRawValue[] = []
    let changed: LeadRawValue[] = []
    let removed: LeadRawValue[] = []

    // created, x ∈ form ∆ initial
    created = newLeadInfo.rawValues.filter(r => r.value != '' || r.description != '')
      .filter(r => !oldLeadInfo.rawValues.map(c => c.id).includes(r.id))

    // modified, x ∈ initial ∩ form by id
    changed = newLeadInfo.rawValues.filter(r => {
      let initialRawvalue = oldLeadInfo.rawValues.find(i => i.id == r.id)
      if (!initialRawvalue) return false; // created

      return !(initialRawvalue.value == r.value && initialRawvalue.description == r.description)
    })

    // removed, initial ∩ form' per id
    removed = oldLeadInfo.rawValues.filter(r => !newLeadInfo.rawValues.map(r => r.id).includes(r.id))

    return [created, changed, removed]
  }

  private getContactsChanges(newLeadInfo: Lead, oldLeadInfo: Lead, type: ContactType): [created: Contact[], changed: Contact[], removed: Contact[]] {
    let created: Contact[] = []
    let changed: Contact[] = []
    let removed: Contact[] = []

    // created, x ∈ form ∆ initial
    created = newLeadInfo.contacts
      .filter(c => c.type == type)
      .filter(c => c.value != '' || c.description != '')
      .filter(c => !oldLeadInfo.contacts.filter(c => c.type == type).map(c => c.id).includes(c.id))

    // modified, x ∈ initial ∩ form by id
    changed = newLeadInfo.contacts
      .filter(c => c.type == type)
      .filter(c => {
        let initialContact = oldLeadInfo.contacts.filter(c => c.type == type).find(i => i.id == c.id)
        if (!initialContact) return false; // created

        return !(initialContact.value == c.value && initialContact.description == c.description)
      })

    // removed, initial ∩ form' per id
    removed = oldLeadInfo.contacts.filter(c => c.type == type).filter(c => !newLeadInfo.contacts.filter(c => c.type == type).map(r => r.id).includes(c.id))

    return [created, changed, removed]
  }

  private getFiscalIdsChanges(newLeadInfo: Lead, oldLeadInfo: Lead, type: FiscalIdKind) {
    let created: FiscalId[] = []
    let changed: FiscalId[] = []
    let removed: FiscalId[] = []

    const oldId = oldLeadInfo.fiscalIds.find(f => f.kind == type)
    const newId = newLeadInfo.fiscalIds.find(f => f.kind == type)
    if (!oldId && newId) created.push(newId)
    else if (oldId && newId && oldId?.value != newId?.value) {
      created.push(newId)
      removed.push(oldId)
    }
    else if (oldId && newId && (oldId?.description != newId?.description || oldId?.isPreferred != newId?.isPreferred))
      changed.push(newId)
    else if (oldId && (!newId || !newId?.value)) removed.push(oldId)

    return [created, changed, removed]
  }
}
